import { useState } from "react";
import { useContent } from "@/context/ContentContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Linkedin, Github } from "lucide-react";

export default function Contact() {
  const { content } = useContent();
  const c = content || {};
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. I'll be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="contact-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">[ 06 · contact ]</div>
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Let's have a conversation.
          </h1>
          <p className="mt-6 text-muted-foreground text-lg">{c.contact_blurb}</p>

          <div className="mt-10 space-y-4">
            <a href={`mailto:${c.contact_email}`} className="flex items-center gap-3 text-sm link-underline" data-testid="contact-email-link">
              <Mail className="w-4 h-4 text-accent" /> {c.contact_email}
            </a>
            <a href={c.contact_linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm link-underline">
              <Linkedin className="w-4 h-4 text-accent" /> linkedin.com/in/marcusacylar
            </a>
            <a href={c.contact_github} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm link-underline">
              <Github className="w-4 h-4 text-accent" /> github.com/marcusacylar
            </a>
            {c.contact_location && (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 text-accent" /> {c.contact_location}
              </div>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name" testid="contact-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Email" testid="contact-email-input" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          </div>
          <Field label="Subject" testid="contact-subject-input" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} required />
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Message</label>
            <textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              rows={7}
              className="mt-2 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 text-base transition-colors duration-200 resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-foreground text-background font-mono text-xs uppercase tracking-[0.2em] hover:opacity-90 transition-opacity duration-200 disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send message →"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 text-base transition-colors duration-200"
      />
    </div>
  );
}
